import cn from 'classnames';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import openSlotLayoutBuilderOperation from 'editor/src/store/app/operation/openSlotLayoutBuilderOperation';
import applySlotTemplateOperation from 'editor/src/store/design/operation/applySlotTemplateOperation';
import removeSlotLayoutOperation from 'editor/src/store/design/operation/removeSlotLayoutOperation';
import { SlotLayout } from 'editor/src/store/design/types';
import { getUsedSlotFormats } from 'editor/src/store/design/util/slotLayout/computeSlotFrames';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import useTr from 'editor/src/util/useTr';

import Button from 'editor/src/component/Button';
import ConfirmationMenu from 'editor/src/component/ConfirmationMenu';
import IconBin from 'editor/src/component/Icon/IconBin';
import IconEdit from 'editor/src/component/Icon/IconEdit';
import IconLayouts from 'editor/src/component/Icon/IconLayouts';

import styles from './index.module.scss';

interface CardProps {
  spreadIndex: number;
  layout: SlotLayout;
}

function SlotLayoutCard({ spreadIndex, layout }: CardProps) {
  const tr = useTr();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formats = getUsedSlotFormats(layout.rows);
  // The picklist can be renamed in its own settings; the card shows that name.
  const name = useSelector(
    (state) => state.design.designData?.spreads[spreadIndex]?.conditionGroup?.conditions[layout.conditionId]?.name,
  );
  // The layout takes the hand-designed slot layers with it, so the bin asks first.
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const remove = () => {
    setConfirmingRemove(false);
    dispatch(removeSlotLayoutOperation(spreadIndex, layout.id));
  };

  return (
    <div className={cn(styles.SlotLayoutCard, 'cy-slot-layout-card')}>
      <div className={styles.header}>
        <IconLayouts className={styles.icon} />
        <div className={styles.name}>{name ?? layout.name}</div>
        <Button
          transparent
          noPadding
          className="cy-slot-layout-edit"
          ariaLabel={t('editor-edit')}
          onClick={() => dispatch(openSlotLayoutBuilderOperation(layout.id))}
        >
          <IconEdit />
        </Button>
        <Button
          transparent
          noPadding
          className="cy-slot-layout-remove"
          ariaLabel={t('editor-remove')}
          onClick={() => setConfirmingRemove(true)}
        >
          <IconBin />
        </Button>
      </div>
      {confirmingRemove && (
        <ConfirmationMenu
          headerText={tr('editor-slot-layout-remove-title', 'Remove this product layout?')}
          headerDescriptionText={tr(
            'editor-slot-layout-remove-description',
            'Its picklists, picture layers and the slot designs go with it. Undo brings them back.',
          )}
          confirmButtonText={t('editor-remove')}
          cancellButtonText={t('editor-cancel')}
          onCrossClick={() => setConfirmingRemove(false)}
          onConfirm={remove}
          onCancel={() => setConfirmingRemove(false)}
        />
      )}
      <div className={styles.meta}>
        {tr('editor-slot-layout-meta', '{{counts}} layouts · {{frames}} positions', {
          counts: layout.rows.length,
          frames: layout.frames.length,
        })}
      </div>
      <p className={styles.hint}>
        {tr(
          'editor-slot-layout-apply-hint',
          "Pick a count that shows the first product's slot of a format, design that slot on the page, " +
            'then apply it to every slot of that format.',
        )}
      </p>
      <p className={styles.hint}>
        {tr(
          'editor-slot-layout-name-hint',
          'Name a text the same in every format, such as "Price" in the wide and the square slot, ' +
            'so a product keeps its value when its slot changes shape.',
        )}
      </p>
      <div className={styles.actions}>
        {formats.map((columns) => (
          <Button
            key={columns}
            secondary
            small
            className="cy-slot-layout-apply"
            onClick={() => dispatch(applySlotTemplateOperation(spreadIndex, layout.id, columns))}
          >
            {tr('editor-slot-layout-apply', 'Apply to {{count}}-per-row slots', { count: columns })}
          </Button>
        ))}
      </div>
    </div>
  );
}

/** The product layouts of the spread being viewed; nothing when it has none. */
function SlotLayoutCards() {
  const { spreadIndex, layouts } = useSelector((state) => {
    const index = getCurrentSpreadIndex(state);
    return { spreadIndex: index, layouts: state.design.designData?.spreads[index]?.slotLayouts };
  }, shallowEqual);

  if (!layouts?.length) {
    return null;
  }
  return (
    <div className={styles.list}>
      {layouts.map((layout) => (
        <SlotLayoutCard key={layout.id} spreadIndex={spreadIndex} layout={layout} />
      ))}
    </div>
  );
}

export default React.memo(SlotLayoutCards);
